import { FC, useRef, useLayoutEffect, useMemo, useState, useCallback } from 'react';
import Slider, { Settings } from 'react-slick';
import { Box, Button, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { SliderProps } from './types';
// eslint-disable-next-line import/order
import styles from './slider-bottom-right-arrow.styles.module.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export const SliderBottomRightArrow: FC<SliderProps> = ({
  title,
  description,
  children,
  settings,
  slideToShow,
  onGoToListingPage,
  onLastSlideClick,
}) => {
  const { genericResponsiveValue } = useResponsiveStyle();

  const [currentSlide, setCurrentSlide] = useState(0);
  const { isMobile, isTablet, isDesktopLarge } = useBreakingPoints();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const sliderRef = useRef<Slider | null>(null);

  const arrowSize = convertSizeToNumber(`${useThemeValue('sizes', 'm')}`) || 16;
  const arrowGap = convertSizeToNumber(`${useThemeValue('sizes', 'xs')}`) || 0;

  const slideToShowResponsive = useMemo(() => {
    if (isMobile) {
      return slideToShow?.mobile ?? 1;
    }
    if (isTablet) {
      return slideToShow?.tablet ?? 2.5;
    }
    if (isDesktopLarge) {
      return slideToShow?.desktopLarge ?? 4.5;
    }
    return slideToShow?.desktop ?? 3.5;
  }, [
    isMobile,
    isTablet,
    isDesktopLarge,
    slideToShow?.desktop,
    slideToShow?.mobile,
    slideToShow?.tablet,
    slideToShow?.desktopLarge,
  ]);
  const slideCount = children?.length ?? 0;

  // last index from which the "last view" can start
  const maxStartIndex = useMemo(() => {
    // if slidesToShow is fractional, the last start is still an integer
    const raw = slideCount - slideToShowResponsive;
    return Math.max(0, Math.ceil(raw));
  }, [slideCount, slideToShowResponsive]);

  const isLastSlide = Math.ceil(currentSlide) >= maxStartIndex;
  const isFirstSlide = currentSlide === 0;
  const showArrows = children?.length > slideToShowResponsive;

  const handlePrev = useCallback(() => {
    sliderRef.current?.slickPrev();
  }, []);

  const handleNext = useCallback(() => {
    if (isLastSlide && onLastSlideClick) {
      onLastSlideClick();
    } else {
      sliderRef.current?.slickNext();
    }
  }, [isLastSlide, onLastSlideClick]);

  const sliderSettings: Settings = {
    infinite: false,
    speed: 400,
    slidesToScroll: 1,
    slidesToShow: slideToShowResponsive,
    arrows: false,
    dots: false,
    swipeToSlide: true,
    swipe: true,
    ...settings,
    afterChange: (index: number) => setCurrentSlide(index),
  };

  useLayoutEffect(() => {
    const element = wrapRef.current;
    if (!element) {
      return undefined;
    }
    if (slideToShowResponsive === 1) {
      element.style.setProperty('--bleed-offset', '0px');
      return undefined;
    }

    const update = () => {
      const wrapRight = element.getBoundingClientRect().right;
      const viewportRight = window.innerWidth;

      // space needed to reach the end of the viewport
      const missing = Math.round(Math.max(0, viewportRight - wrapRight));
      if (missing === 0 || element.style.getPropertyValue('--bleed-offset') === `${missing}px`) {
        return;
      }
      element.style.setProperty('--bleed-offset', `${missing}px`);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(element);
    window.addEventListener('resize', update);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [slideToShowResponsive]);

  return (
    <>
      {(title || description || onGoToListingPage) && (
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems={genericResponsiveValue({
            breakPoints: { desktop: description ? 'flex-end' : 'center' },
            defaultValue: 'flex-start',
          })}
          mb='s'
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
        >
          <Box flex='1'>
            <Typography variant='h3'>{title}</Typography>
            <Typography variant='p3/light'>{description}</Typography>
          </Box>
          {children?.length > slideToShowResponsive && onGoToListingPage ? (
            <Button
              onClick={onGoToListingPage.onClick}
              textAlign='left'
              variant='link/primary'
              mt='none'
              alignItems={description ? 'flex-end' : undefined}
            >
              <Box display='flex' alignItems='center'>
                <Typography ml='xxxs' mr='xxxs' variant='p2'>
                  {onGoToListingPage.text}
                </Typography>
                <Svg name='arrowRight' size='xs' />
              </Box>
            </Button>
          ) : (
            <Box />
          )}
        </Box>
      )}

      <Box position='relative' className={styles.bleedRight} ref={wrapRef}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <Slider ref={sliderRef} {...sliderSettings} className={styles.slider}>
          {children?.map((child, index) => {
            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                pr={slideToShowResponsive <= 1 ? 'none' : 's'}
              >
                <Box display='flex' width='100%' height='100%'>
                  {child}
                </Box>
              </Box>
            );
          })}
        </Slider>
      </Box>

      {showArrows && (
        <Box
          display='flex'
          justifyContent='flex-end'
          alignItems='center'
          mt='xs'
          mb='xxl'
          style={{ gap: arrowGap }}
        >
          <Button
            aria-label='Previous'
            onClick={handlePrev}
            minWidth='fit-content'
            width='fit-content'
            height='fit-content'
            padding='none'
            background='transparent'
            disabled={isFirstSlide}
          >
            <Svg name='chevronLeft' size={arrowSize} />
          </Button>
          <Button
            aria-label='Next'
            onClick={handleNext}
            minWidth='fit-content'
            width='fit-content'
            height='fit-content'
            padding='none'
            background='transparent'
            disabled={!onLastSlideClick && isLastSlide}
          >
            <Svg name='chevronRight' size={arrowSize} />
          </Button>
        </Box>
      )}
    </>
  );
};
